import { useEffect, useRef, useState } from 'react';

import { pixelThumb, rawUrl } from '../../../lib/thumbnails.js';
import { dragStencil, stencilDragMode, usePaint, type PaintStencil, type StencilDragMode } from '../../../state/paint.js';
import { useProjectFiles } from '../../../state/project-files.js';
import { useWorkspace } from '../../../state/workspace.js';
import './paint.css';

const MODE_LABEL: Record<StencilDragMode, string> = { move: 'Move', scale: 'Scale', rotate: 'Rotate' };

/** URL of the stencil image (PNG etc. directly, `.pixel.json` rendered), refreshed when the file changes. */
function useStencilImageUrl(path: string | undefined): string | null {
  const projectId = useWorkspace((s) => s.projectId);
  const version = useProjectFiles((s) => (path ? (s.entries.find((e) => e.path === path)?.modifiedAt ?? '') : ''));
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!projectId || !path) {
      setUrl(null);
      return;
    }
    if (!path.endsWith('.pixel.json')) {
      setUrl(rawUrl(projectId, path, version || undefined));
      return;
    }
    let cancelled = false;
    void pixelThumb(projectId, path, version).then((u) => {
      if (!cancelled) setUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, path, version]);
  return url;
}

/**
 * Stencil bounds over the model viewport while texture painting with a
 * stencil, plus Blender-style placement: right drag moves it, Shift + right
 * drag scales it, Ctrl + right drag rotates it (Esc cancels; Alt + right drag
 * still orbits). Updates go through `setStencil`, so the Stencil popover
 * numbers follow. Engine coordinates are device pixels; CSS uses CSS pixels.
 */
export function StencilOverlay() {
  const active = usePaint((s) => s.active);
  const stencil = usePaint((s) => s.stencil);
  const ref = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<StencilDragMode | null>(null);
  const enabled = active && stencil !== null;
  const imageUrl = useStencilImageUrl(enabled ? stencil?.image : undefined);

  useEffect(() => {
    if (!enabled) return;
    const canvas = ref.current?.parentElement?.querySelector<HTMLCanvasElement>('canvas.viewport-canvas');
    if (!canvas) return;
    type Start = Pick<PaintStencil, 'center' | 'size' | 'rotation'>;
    let session: { mode: StencilDragMode; from: [number, number]; start: Start; image: string } | null = null;
    let pending: Partial<PaintStencil> | null = null;
    let frame = 0;
    const at = (e: PointerEvent): [number, number] => {
      const r = canvas.getBoundingClientRect();
      const k = window.devicePixelRatio || 1;
      return [(e.clientX - r.left) * k, (e.clientY - r.top) * k];
    };
    const flush = () => {
      frame = 0;
      if (pending) usePaint.getState().setStencil(pending);
      pending = null;
    };
    const finish = (commit: boolean) => {
      if (!session) return;
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      const last = commit ? pending : { image: session.image, ...session.start };
      pending = null;
      session = null;
      setDrag(null);
      if (last) usePaint.getState().setStencil(last);
    };
    const swallow = (e: Event) => {
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    const onDown = (e: PointerEvent) => {
      if (e.button !== 2 || e.target !== canvas || e.altKey) return;
      const st = usePaint.getState().stencil;
      if (!st) return;
      swallow(e);
      const mode = stencilDragMode(e);
      session = { mode, from: at(e), start: { center: st.center, size: st.size, rotation: st.rotation }, image: st.image };
      setDrag(mode);
    };
    const onMove = (e: PointerEvent) => {
      if (!session) return;
      swallow(e);
      pending = { image: session.image, ...dragStencil(session.start, session.mode, session.from, at(e)) };
      if (!frame) frame = requestAnimationFrame(flush);
    };
    const onUp = (e: PointerEvent) => {
      if (!session || e.button !== 2) return;
      swallow(e);
      finish(true);
    };
    const onKey = (e: KeyboardEvent) => {
      if (session && e.key === 'Escape') {
        swallow(e);
        finish(false);
      }
    };
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp, true);
      window.removeEventListener('keydown', onKey, true);
      if (frame) cancelAnimationFrame(frame);
      session = null;
      setDrag(null);
    };
  }, [enabled]);

  if (!enabled || !stencil) return null;
  const k = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const box = {
    left: stencil.center[0] / k,
    top: stencil.center[1] / k,
    width: stencil.size[0] / k,
    height: stencil.size[1] / k,
    transform: `translate(-50%, -50%) rotate(${-stencil.rotation}deg)`,
  };
  return (
    <div ref={ref} className={`paint-stencil-overlay${drag ? ' is-dragging' : ''}`} aria-hidden>
      <div className="paint-stencil-box" style={box}>
        {imageUrl && <img className="paint-stencil-image" src={imageUrl} alt="" draggable={false} />}
        <i className="paint-stencil-up" />
        <i className="paint-stencil-cross" />
      </div>
      <span className="paint-stencil-hint">
        {drag
          ? `${MODE_LABEL[drag]} stencil · ${Math.round(stencil.size[0])}×${Math.round(stencil.size[1])} px · ${stencil.rotation.toFixed(1)}° · Esc cancels`
          : 'Stencil: right drag moves · Shift scales · Ctrl rotates'}
      </span>
    </div>
  );
}
